/**
 * BetterStack Page
 * Displays logs synced from BetterStack API
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDataSourceStore } from '@store/dataSourceStore';
import { api } from '@services/api';
import { useWebSocket } from '@hooks/useWebSocket';
import { Search, RefreshCw, AlertCircle, XCircle, Info, AlertTriangle, X, Sparkles, MessageSquare, FileText, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import DateRangeFilter, { DateRange } from '@components/analytics/DateRangeFilter';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

/**
 * Get HTTP status code description (industry standard)
 */
const getStatusCodeDescription = (code: string): string => {
  const statusCodeMap: Record<string, string> = {
    // 2xx Success
    '200': 'OK',
    '201': 'Created',
    '202': 'Accepted',
    '204': 'No Content',
    '206': 'Partial Content',
    
    // 3xx Redirection
    '300': 'Multiple Choices',
    '301': 'Moved Permanently',
    '302': 'Found',
    '303': 'See Other',
    '304': 'Not Modified',
    '307': 'Temporary Redirect',
    '308': 'Permanent Redirect',
    
    // 4xx Client Error
    '400': 'Bad Request',
    '401': 'Unauthorized',
    '403': 'Forbidden',
    '404': 'Not Found',
    '405': 'Method Not Allowed',
    '406': 'Not Acceptable',
    '408': 'Request Timeout',
    '409': 'Conflict',
    '410': 'Gone',
    '411': 'Length Required',
    '412': 'Precondition Failed',
    '413': 'Payload Too Large',
    '414': 'URI Too Long',
    '415': 'Unsupported Media Type',
    '416': 'Range Not Satisfiable',
    '417': 'Expectation Failed',
    '418': "I'm a teapot",
    '422': 'Unprocessable Entity',
    '425': 'Too Early',
    '426': 'Upgrade Required',
    '428': 'Precondition Required',
    '429': 'Too Many Requests',
    '431': 'Request Header Fields Too Large',
    '451': 'Unavailable For Legal Reasons',
    
    // 5xx Server Error
    '500': 'Internal Server Error',
    '501': 'Not Implemented',
    '502': 'Bad Gateway',
    '503': 'Service Unavailable',
    '504': 'Gateway Timeout',
    '505': 'HTTP Version Not Supported',
    '506': 'Variant Also Negotiates',
    '507': 'Insufficient Storage',
    '508': 'Loop Detected',
    '510': 'Not Extended',
    '511': 'Network Authentication Required',
  };
  
  return statusCodeMap[code] || 'Unknown';
};

interface BetterStackLog {
  _id: string;
  betterstackLogId: string;
  timestamp: string;
  message: string;
  severity?: 'debug' | 'info' | 'notice' | 'warning' | 'error' | 'critical' | 'alert' | 'emergency';
  level?: 'debug' | 'info' | 'notice' | 'warning' | 'error' | 'critical' | 'alert' | 'emergency'; // Backend field name
  source?: string;
  service?: string;
  host?: string;
  metadata?: Record<string, any>;
  syncedAt: string;
}

interface LogsResponse {
  data: {
    logs: BetterStackLog[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface StatsResponse {
  data: {
    totalLogs: number;  // Backend returns totalLogs, not total
    bySeverity: Record<string, number>;
    bySource: Array<{ source: string; count: number }>;
    lastSync: string | null;
  };
  cached?: boolean;
}

const BetterStackPage: React.FC = () => {
  const { selectedCompany, selectedDataSources, getSelection, setSelectedDataSources } = useDataSourceStore();
  const queryClient = useQueryClient();
  const { companyId, dataSourceId } = getSelection();

  // WebSocket for real-time sync updates
  const { socket, connected } = useWebSocket({ autoConnect: true });

  // Fetch fresh company data to get latest data sources
  const { data: companyData } = useQuery({
    queryKey: ['company', companyId],
    queryFn: async () => {
      if (!companyId) return null;
      const response = await api.get(`/api/v1/companies/${companyId}`);
      return response.data.data;
    },
    enabled: !!companyId,
    staleTime: 30000, // 30 seconds
  });

  // Filter BetterStack data sources from FRESH company data (not cached store)
  const betterstackDataSources = (companyData?.dataSources || []).filter(
    (ds: any) => ds.isActive && ds.availableModules?.includes('betterstack')
  );
  
  // Auto-select BetterStack data source if available and not already selected
  React.useEffect(() => {
    if (betterstackDataSources.length > 0 && companyData) {
      const currentBetterStack = selectedDataSources.find(
        (ds) => ds.isActive && ds.availableModules?.includes('betterstack')
      );
      
      // If no BetterStack source selected, or selected one doesn't exist in fresh data, auto-select
      if (!currentBetterStack || !betterstackDataSources.find((ds: any) => ds.id === currentBetterStack.id)) {
        const defaultBetterStack = betterstackDataSources.find((ds: any) => ds.isDefault) || betterstackDataSources[0];
        if (defaultBetterStack) {
          // Update store with fresh data source
          const otherSources = selectedDataSources.filter(
            (ds) => !ds.availableModules?.includes('betterstack')
          );
          setSelectedDataSources([...otherSources, defaultBetterStack]);
        }
      }
    }
  }, [betterstackDataSources, companyData, selectedDataSources, setSelectedDataSources]);

  // Use fresh data source if available, otherwise fall back to store
  const selectedDataSource = React.useMemo(() => {
    // First, try to find a BetterStack source in the currently selected sources
    const bsFromSelected = betterstackDataSources.find(
      (ds: any) => selectedDataSources.some(sds => sds.id === ds.id)
    );
    
    // If found, return it
    if (bsFromSelected) return bsFromSelected;
    
    // If not found but BetterStack sources exist, auto-select the first one
    if (betterstackDataSources.length > 0) {
      const defaultBS = betterstackDataSources.find((ds: any) => ds.isDefault) || betterstackDataSources[0];
      console.log('🔧 Auto-selecting BetterStack data source:', defaultBS);
      return defaultBS;
    }
    
    return null;
  }, [betterstackDataSources, selectedDataSources]);
  
  // Debug logging
  React.useEffect(() => {
    console.log('🔍 BetterStack Page Debug:', {
      selectedDataSourcesCount: selectedDataSources.length,
      selectedDataSources: selectedDataSources.map(ds => ({ id: ds.id, type: ds.type, availableModules: ds.availableModules })),
      betterstackDataSourcesCount: betterstackDataSources.length,
      betterstackDataSources: betterstackDataSources.map(ds => ({ id: ds.id, type: ds.type })),
      selectedDataSourceId: selectedDataSource?.id,
      companyId,
    });
  }, [selectedDataSources, betterstackDataSources, selectedDataSource, companyId]);

  // State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [methodFilter, setMethodFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [serviceFilter, setServiceFilter] = useState<string>('');
  const [hostFilter, setHostFilter] = useState<string>('');
  const [statusCodeFilter, setStatusCodeFilter] = useState<string>('');
  const [botFilter, setBotFilter] = useState<string>(''); // 'all', 'bots', 'humans'
  
  // Debounce search query for better performance
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(1); // Reset to first page when search changes
    }, 300); // 300ms debounce delay
    
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Date range state (default: Last 14 days to match backend)
  const getDefaultDateRange = (): DateRange => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 14);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      label: 'Last 14 days',
    };
  };

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const saved = localStorage.getItem('betterstack_dateRange');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (_) {
        return getDefaultDateRange();
      }
    }
    return getDefaultDateRange();
  });

  // Save date range to localStorage when it changes
  React.useEffect(() => {
    localStorage.setItem('betterstack_dateRange', JSON.stringify(dateRange));
  }, [dateRange]);

  const [sortBy, setSortBy] = useState<string>('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedLog, setSelectedLog] = useState<BetterStackLog | null>(null);
  const [detailTab, setDetailTab] = useState<'overview' | 'context' | 'explain' | 'comments'>('overview');
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  
  // Export modal state
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState<{
    total: number;
    fetched: number;
    currentPage: number;
    totalPages: number;
    files: Array<{ index: number; name: string; logs: number; blob: Blob | null; progress: number }>;
    currentFileIndex: number;
    status: 'fetching' | 'processing' | 'completed' | 'error';
    error?: string;
  } | null>(null);

  // Fetch logs
  const {
    data: logsData,
    isLoading: logsLoading,
    error: logsError,
    refetch: refetchLogs,
  } = useQuery<LogsResponse>({
    queryKey: [
      'betterstack-logs',
      companyId,
      selectedDataSource?.id,
      page,
      limit,
      debouncedSearchQuery,
      severityFilter,
      methodFilter,
      sourceFilter,
      serviceFilter,
      hostFilter,
      statusCodeFilter,
      botFilter,
      dateRange.startDate,
      dateRange.endDate,
      sortBy,
      sortOrder,
    ],
    queryFn: async () => {
      if (!companyId || !selectedDataSource?.id) {
        return null;
      }

      const params: any = {
        companyId,
        dataSourceId: selectedDataSource.id,
        page,
        limit,
        sortBy,
        sortOrder,
        from: dateRange.startDate,
        to: dateRange.endDate,
      };

      if (debouncedSearchQuery) params.search = debouncedSearchQuery;
      if (severityFilter) params.severity = severityFilter;
      if (methodFilter) params.method = methodFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (serviceFilter) params.service = serviceFilter;
      if (hostFilter) params.host = hostFilter;
      if (statusCodeFilter) params.statusCode = statusCodeFilter;
      if (botFilter && botFilter !== 'all') params.bot = botFilter;

      const response = await api.get('/api/v1/betterstack/logs', { params });
      return response.data;
    },
    enabled: !!companyId && !!selectedDataSource?.id,
    staleTime: 30000, // 30 seconds
  });

  // Fetch stats (with date range)
  const { data: statsData } = useQuery<StatsResponse>({
    queryKey: ['betterstack-stats', companyId, selectedDataSource?.id, dateRange.startDate, dateRange.endDate],
    queryFn: async () => {
      if (!companyId || !selectedDataSource?.id) {
        return null;
      }

      const response = await api.get('/api/v1/betterstack/stats', {
        params: {
          companyId,
          dataSourceId: selectedDataSource.id,
          from: dateRange.startDate,
          to: dateRange.endDate,
        },
      });
      return response.data;
    },
    enabled: !!companyId && !!selectedDataSource?.id,
    staleTime: 60000, // 1 minute
  });

  // Fetch filter options (all unique values from all logs, not filtered)
  const { data: filterOptionsData } = useQuery<{
    data: {
      severities: string[];
      methods: string[];
      sources: string[];
      services: string[];
      hosts: string[];
      statusCodes: string[];
      botTypes: string[];
    };
  }>({
    queryKey: ['betterstack-filter-options', companyId, selectedDataSource?.id, dateRange.startDate, dateRange.endDate],
    queryFn: async () => {
      if (!companyId || !selectedDataSource?.id) {
        return null;
      }

      const response = await api.get('/api/v1/betterstack/filter-options', {
        params: {
          companyId,
          dataSourceId: selectedDataSource.id,
          from: dateRange.startDate,
          to: dateRange.endDate,
        },
      });
      return response.data;
    },
    enabled: !!companyId && !!selectedDataSource?.id,
    staleTime: 120000, // 2 minutes - filter options don't change often
  });

  // Calculate interval based on date range (for query key and API call)
  const calculateInterval = React.useMemo(() => {
    if (!dateRange.startDate || !dateRange.endDate) return 'hour';
    
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    // Calculate days difference
    const daysDiff = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    
    // Hourly for ranges <= 3 days (including exactly 3 days), daily for > 3 days
    // Using > 3.1 to account for rounding and ensure 3 full days shows hourly
    const interval = daysDiff > 3.1 ? 'day' : 'hour';
    
    console.log('📊 Interval calculation:', {
      daysDiff: daysDiff.toFixed(3),
      interval,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
    
    return interval;
  }, [dateRange.startDate, dateRange.endDate]);

  // Fetch histogram data
  const { data: histogramData } = useQuery<{
    data: {
      data: Array<{
        timestamp: string;
        count: number;
        statusCodes: Record<string, number>;
      }>;
      interval: string;
      from: string;
      to: string;
    };
    cached?: boolean;
  }>({
    queryKey: ['betterstack-histogram', companyId, selectedDataSource?.id, dateRange.startDate, dateRange.endDate, calculateInterval],
    queryFn: async () => {
      if (!companyId || !selectedDataSource?.id) {
        return null;
      }

      const daysDiff = (new Date(dateRange.endDate).getTime() - new Date(dateRange.startDate).getTime()) / (1000 * 60 * 60 * 24);
      console.log('📊 Histogram API call:', {
        daysDiff: daysDiff.toFixed(3),
        interval: calculateInterval,
        from: dateRange.startDate,
        to: dateRange.endDate,
        expectedInterval: daysDiff > 3.1 ? 'day' : 'hour',
      });

      const response = await api.get('/api/v1/betterstack/histogram', {
        params: {
          companyId,
          dataSourceId: selectedDataSource.id,
          from: dateRange.startDate,
          to: dateRange.endDate,
          interval: calculateInterval,
        },
      });
      return response.data;
    },
    enabled: !!companyId && !!selectedDataSource?.id,
    staleTime: 60000, // 1 minute
  });

  // Subscribe to BetterStack sync events via WebSocket for live updates
  useEffect(() => {
    if (!socket || !connected || !companyId || !selectedDataSource?.id) return;

    const room = `betterstack:${companyId}:${selectedDataSource.id}`;
    
    console.log(`📡 BetterStack subscribing to sync room: ${room}`);
    
    // Subscribe to BetterStack sync events
    socket.emit('analytics:subscribe', {
      companyId,
      dataSourceId: selectedDataSource.id,
      room,
    });

    // Listen for sync completion events
    const handleSyncCompleted = (data: any) => {
      console.log('🔄 BetterStack sync completed:', data);
      
      if (data.success && data.newLogs > 0) {
        toast.success(`🔄 ${data.newLogs} new logs synced!`, {
          duration: 3000,
          icon: '🔄',
        });
        
        // Invalidate and refetch queries to show new logs
        queryClient.invalidateQueries({ queryKey: ['betterstack-logs'] });
        queryClient.invalidateQueries({ queryKey: ['betterstack-stats'] });
        queryClient.invalidateQueries({ queryKey: ['betterstack-filter-options'] });
      } else if (data.success && data.updatedLogs > 0) {
        // Silently refresh if only updates (no toast)
        queryClient.invalidateQueries({ queryKey: ['betterstack-logs'] });
        queryClient.invalidateQueries({ queryKey: ['betterstack-stats'] });
      } else if (!data.success && data.error) {
        toast.error(`Sync failed: ${data.error}`, {
          duration: 5000,
        });
      }
    };

    socket.on('betterstack:sync:completed', handleSyncCompleted);

    return () => {
      socket.off('betterstack:sync:completed', handleSyncCompleted);
      socket.emit('analytics:unsubscribe', { companyId, room });
    };
  }, [socket, connected, companyId, selectedDataSource?.id, queryClient]);

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: async () => {
      if (!companyId || !selectedDataSource?.id) {
        throw new Error('Company and data source must be selected');
      }

      console.log('🔄 Syncing BetterStack logs:', {
        companyId,
        dataSourceId: selectedDataSource.id,
        dataSourceName: selectedDataSource.name,
        dataSourceType: selectedDataSource.type,
        dateRange: dateRange.label,
        from: dateRange.startDate,
        to: dateRange.endDate,
      });
      
      const response = await api.post('/api/v1/betterstack/sync', {
        companyId,
        dataSourceId: selectedDataSource.id,
        limit: 1000, // Increased limit for better sync
        from: dateRange.startDate,
        to: dateRange.endDate,
        syncAllPages: true, // Sync all pages automatically
      });
      return response.data;
    },
    onSuccess: (data: any) => {
      if (data?.data?.newLogs === 0 && data?.data?.updatedLogs === 0) {
        toast.error(
          <div className="max-w-md">
            <div className="font-semibold mb-2">No logs found</div>
            <div className="text-sm">
              <p>The sync completed but no logs were fetched. Possible reasons:</p>
              <ul className="list-disc list-inside space-y-1 mt-2 ml-2">
                <li>Query API credentials are missing or incorrect</li>
                <li>No logs exist in BetterStack for your source</li>
                <li>The time range has no logs</li>
              </ul>
              <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                Check your backend logs for more details.
              </p>
            </div>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.success(`Logs synced successfully: ${data?.data?.newLogs || 0} new, ${data?.data?.updatedLogs || 0} updated`);
      }
      queryClient.invalidateQueries({ queryKey: ['betterstack-logs'] });
      queryClient.invalidateQueries({ queryKey: ['betterstack-stats'] });
    },
    onError: (error: any) => {
      const errorData = error.response?.data;
      const errorMessage = errorData?.message || 'Failed to sync logs';
      
      console.error('❌ BetterStack sync error:', {
        errorMessage,
        errorData,
        companyId,
        dataSourceId: selectedDataSource?.id,
        selectedDataSource,
      });
      
      // Check if it's a missing Query API credentials error
      if (errorData?.error?.code === 'MISSING_QUERY_API_CREDENTIALS' || errorMessage.includes('Query API credentials')) {
        toast.error(
          <div className="max-w-md">
            <div className="font-semibold mb-2">Query API Credentials Required</div>
            <div className="text-sm space-y-1">
              <p>To sync logs, you need to configure Query API credentials:</p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Go to BetterStack Dashboard → Dashboards → Connect remotely</li>
                <li>Click "Connect ClickHouse HTTP client"</li>
                <li>Copy the endpoint URL, username, and password</li>
                <li>Add them to your data source configuration</li>
              </ol>
              <a
                href={errorData?.error?.documentation || 'https://betterstack.com/docs/logs/query-api/v2/dashboards/'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 underline mt-2 inline-block"
              >
                View documentation →
              </a>
            </div>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.error(errorMessage);
      }
    },
  });

  // Export to CSV function with modal and progress
  const exportToCSV = async () => {
    try {
      if (!companyId || !selectedDataSource?.id) {
        toast.error('Company and data source must be selected');
        return;
      }

      // Build base params with current filters
      const baseParams: any = {
        companyId,
        dataSourceId: selectedDataSource.id,
        sortBy,
        sortOrder,
        from: dateRange.startDate,
        to: dateRange.endDate,
      };

      if (debouncedSearchQuery) baseParams.search = debouncedSearchQuery;
      if (severityFilter) baseParams.severity = severityFilter;
      if (methodFilter) baseParams.method = methodFilter;
      if (sourceFilter) baseParams.source = sourceFilter;
      if (serviceFilter) baseParams.service = serviceFilter;
      if (hostFilter) baseParams.host = hostFilter;
      if (statusCodeFilter) baseParams.statusCode = statusCodeFilter;
      if (botFilter && botFilter !== 'all') baseParams.bot = botFilter;

      // Initialize export progress
      const MAX_LOGS_PER_FILE = 10000;
      setExportModalOpen(true);
      setExportProgress({
        total: 0,
        fetched: 0,
        currentPage: 0,
        totalPages: 0,
        files: [],
        currentFileIndex: 0,
        status: 'fetching',
      });

      // First, get the total count
      const firstPageResponse = await api.get('/api/v1/betterstack/logs', {
        params: { ...baseParams, page: 1, limit: 1000 },
      });
      
      const total = firstPageResponse.data?.data?.total || 0;
      const totalPages = firstPageResponse.data?.data?.totalPages || 1;

      if (total === 0) {
        setExportProgress(prev => prev ? { ...prev, status: 'error', error: 'No logs to export' } : null);
        return;
      }

      // Calculate number of files needed
      const numFiles = Math.ceil(total / MAX_LOGS_PER_FILE);
      const files = Array.from({ length: numFiles }, (_, i) => ({
        index: i + 1,
        name: `betterstack-logs-${new Date().toISOString().split('T')[0]}-part${i + 1}.csv`,
        logs: 0,
        blob: null as Blob | null,
        progress: 0,
      }));

      setExportProgress({
        total,
        fetched: 0,
        currentPage: 1,
        totalPages,
        files,
        currentFileIndex: 0,
        status: 'fetching',
      });

      // Fetch all pages
      let allLogs: BetterStackLog[] = [];
      
      for (let page = 1; page <= totalPages; page++) {
        const response = await api.get('/api/v1/betterstack/logs', {
          params: { ...baseParams, page, limit: 1000 },
        });
        
        const pageLogs = response.data?.data?.logs || [];
        allLogs = [...allLogs, ...pageLogs];
        
        setExportProgress(prev => prev ? {
          ...prev,
          fetched: allLogs.length,
          currentPage: page,
        } : null);
      }

      // Process logs into files
      setExportProgress(prev => prev ? { ...prev, status: 'processing' } : null);

      const escapeCSV = (value: any) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        // CSV escaping: if field contains comma, quote, or newline, wrap in quotes and double any quotes
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          // Replace any existing double quotes with single quotes first, then escape properly
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      // Extract actual message from JSON if message field is JSON
      const extractMessage = (message: string): string => {
        if (!message) return '';
        try {
          // Try to parse as JSON
          const parsed = JSON.parse(message);
          // If it has a "message" property, use that (it's the actual log message)
          if (parsed.message && typeof parsed.message === 'string') {
            // Remove BOM (Byte Order Mark) and other invisible characters from the start
            return parsed.message.replace(/^\uFEFF|\u200B|\u200C|\u200D/g, '').trim();
          }
          // Otherwise return the original message
          return message.replace(/^\uFEFF|\u200B|\u200C|\u200D/g, '').trim();
        } catch (_) {
          // Not JSON, return as-is but clean up invisible characters
          return message.replace(/^\uFEFF|\u200B|\u200C|\u200D/g, '').trim();
        }
      };

      const headers = ['Time', 'Severity', 'Method', 'Message', 'Source', 'Service', 'Host', 'Bot'];
      const dateStr = new Date().toISOString().split('T')[0];

      // Process each file
      for (let fileIndex = 0; fileIndex < numFiles; fileIndex++) {
        const startIdx = fileIndex * MAX_LOGS_PER_FILE;
        const endIdx = Math.min(startIdx + MAX_LOGS_PER_FILE, allLogs.length);
        const fileLogs = allLogs.slice(startIdx, endIdx);

        setExportProgress(prev => prev ? {
          ...prev,
          currentFileIndex: fileIndex,
          files: prev.files.map((f, i) => 
            i === fileIndex ? { ...f, progress: 0 } : f
          ),
        } : null);

        const csvRows = [
          headers.join(','),
          ...fileLogs.map((log: BetterStackLog, idx: number) => {
            const { service: extractedService, host: extractedHost } = extractServiceAndHost(log.message);
            const service = log.service || extractedService || '';
            const host = log.host || extractedHost || '';

            // Update progress for this file
            if (idx % 100 === 0) {
              setExportProgress(prev => prev ? {
                ...prev,
                files: prev.files.map((f, i) => 
                  i === fileIndex ? { ...f, progress: Math.round((idx / fileLogs.length) * 100) } : f
                ),
              } : null);
            }

            // Extract actual message content (handle JSON format)
            const actualMessage = extractMessage(log.message);
            const methodMatch = actualMessage.match(/^"?(GET|POST|PUT|DELETE|PATCH|HEAD|OPTIONS)\s/i);
            const method = methodMatch ? methodMatch[1] : '';
            const { botName } = detectBotType(log.message);
            
            return [
              escapeCSV(new Date(log.timestamp).toLocaleString('en-GB')),
              escapeCSV(log.severity || log.level || ''),
              escapeCSV(method),
              escapeCSV(actualMessage),
              escapeCSV(log.source || ''),
              escapeCSV(service),
              escapeCSV(host),
              escapeCSV(botName || ''),
            ].join(',');
          }),
        ];

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        setExportProgress(prev => prev ? {
          ...prev,
          files: prev.files.map((f, i) => 
            i === fileIndex ? { ...f, logs: fileLogs.length, blob, progress: 100 } : f
          ),
        } : null);
      }

      setExportProgress(prev => prev ? { ...prev, status: 'completed' } : null);
    } catch (error: any) {
      console.error('Export to CSV error:', error);
      setExportProgress(prev => prev ? {
        ...prev,
        status: 'error',
        error: error?.response?.data?.message || 'Failed to export CSV',
      } : null);
    }
  };

  // Download a specific file from export
  const downloadExportFile = (fileIndex: number) => {
    if (!exportProgress?.files[fileIndex]?.blob) return;
    
    const file = exportProgress.files[fileIndex];
    const link = document.createElement('a');
    const url = URL.createObjectURL(file.blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', file.name);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download all files
  const downloadAllFiles = () => {
    if (!exportProgress) return;
    
    exportProgress.files.forEach((file, index) => {
      setTimeout(() => {
        downloadExportFile(index);
      }, index * 200); // Stagger downloads
    });
  };

  // Handle sort
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Get severity icon
  const getSeverityIcon = (severity?: string) => {
    const normalizedSeverity = severity?.toLowerCase();
    switch (normalizedSeverity) {
      case 'critical':
      case 'emergency':
      case 'alert':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'notice':
      case 'info':
        return <Info className="w-4 h-4 text-blue-500" />;
      case 'debug':
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Info className="w-4 h-4 text-gray-400" />;
    }
  };

  // Parse message JSON if possible
  const parseMessage = (message: string): { parsed: any; isJson: boolean } => {
    try {
      const parsed = JSON.parse(message);
      return { parsed, isJson: true };
    } catch (_) {
      return { parsed: message, isJson: false };
    }
  };

  // Extract HTTP status code from log message
  const extractHttpInfo = (message: string): { statusCode?: number; method?: string; path?: string; message: string } => {
    // Common Apache/Nginx log format: IP - - [timestamp] "METHOD PATH PROTOCOL" STATUS
    const httpLogPattern = /"([A-Z]+)\s+([^\s"]+)\s+[^"]*"\s+(\d{3})/;
    const match = message.match(httpLogPattern);
    
    if (match) {
      return {
        method: match[1],
        path: match[2],
        statusCode: parseInt(match[3], 10),
        message: message,
      };
    }
    
    // Try to find status code anywhere in the message
    const statusCodeMatch = message.match(/\s(\d{3})\s/);
    if (statusCodeMatch) {
      return {
        statusCode: parseInt(statusCodeMatch[1], 10),
        message: message,
      };
    }
    
    return { message };
  };

  // Get HTTP status code badge color
  const getHttpStatusColor = (statusCode: number): string => {
    if (statusCode >= 200 && statusCode < 300) {
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    } else if (statusCode >= 300 && statusCode < 400) {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
    } else if (statusCode >= 400 && statusCode < 500) {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
    } else if (statusCode >= 500) {
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    }
    return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
  };

  // Get HTTP method badge color
  const getHttpMethodColor = (method: string): string => {
    const normalizedMethod = method.toUpperCase();
    switch (normalizedMethod) {
      case 'GET':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'POST':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'PUT':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'DELETE':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'PATCH':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      case 'HEAD':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
      case 'OPTIONS':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  // Get filter options - ONLY use API data (from ALL records in database)
  // NEVER fallback to paginated logs - that would show only current page options
  const getFilterOptions = () => {
    // ONLY use API-provided filter options (all available values from ALL records)
    if (filterOptionsData?.data) {
      return filterOptionsData.data;
    }

    // Return empty options if API data not available
    // This ensures we don't show incorrect options from paginated logs
    return {
      severities: [],
      methods: [],
      sources: [],
      services: [],
      hosts: [],
      statusCodes: [],
      botTypes: [],
    };
  };

  // Extract service and host from log message
  const extractServiceAndHost = (message: string): { service?: string; host?: string } => {
    try {
      const parsed = JSON.parse(message);
      if (typeof parsed === 'object' && parsed !== null) {
        const service = parsed.syslog?.appname || parsed.syslog?.app || parsed.service || parsed.app;
        const host = parsed.syslog?.hostname || parsed.syslog?.host || parsed.hostname || parsed.host;
        return {
          service: service ? String(service) : undefined,
          host: host ? String(host) : undefined,
        };
      }
    } catch (_) {
      // Not JSON, return empty
    }
    return {};
  };

  // Detect bot type from message and return bot name
  const detectBotType = (message: string): { isBot: boolean; botName: string | null } => {
    try {
      if (!message || typeof message !== 'string') return { isBot: false, botName: null };
      
      const ua = message.toLowerCase();
      
      // Check for specific bot types (order matters - more specific first)
      if (ua.includes('googlebot')) return { isBot: true, botName: 'Googlebot' };
      if (ua.includes('bingbot') || ua.includes('msnbot')) return { isBot: true, botName: 'Bingbot' };
      if (ua.includes('facebookbot') || ua.includes('facebookexternalhit') || ua.includes('facebot')) return { isBot: true, botName: 'Facebook Bot' };
      if (ua.includes('twitterbot')) return { isBot: true, botName: 'Twitter Bot' };
      if (ua.includes('linkedinbot')) return { isBot: true, botName: 'LinkedIn Bot' };
      if (ua.includes('baiduspider')) return { isBot: true, botName: 'Baidu Spider' };
      if (ua.includes('yandexbot')) return { isBot: true, botName: 'Yandex Bot' };
      if (ua.includes('duckduckbot')) return { isBot: true, botName: 'DuckDuckGo Bot' };
      if (ua.includes('slurp') || ua.includes('yahoo')) return { isBot: true, botName: 'Yahoo Slurp' };
      if (ua.includes('sogou')) return { isBot: true, botName: 'Sogou' };
      if (ua.includes('ahrefsbot') || ua.includes('ahrefs')) return { isBot: true, botName: 'Ahrefs Bot' };
      if (ua.includes('semrushbot') || ua.includes('semrush')) return { isBot: true, botName: 'SEMrush Bot' };
      if (ua.includes('mj12bot')) return { isBot: true, botName: 'MJ12 Bot' };
      if (ua.includes('exabot')) return { isBot: true, botName: 'ExaBot' };
      if (ua.includes('ia_archiver') || ua.includes('archive.org')) return { isBot: true, botName: 'Archive.org' };
      if (ua.includes('chatgpt') || ua.includes('gptbot')) return { isBot: true, botName: 'ChatGPT' };
      if (ua.includes('openai') || (ua.includes('gpt') && !ua.includes('chatgpt'))) return { isBot: true, botName: 'OpenAI' };
      
      // Generic bots (check last to avoid false positives)
      if (ua.includes('bot') || ua.includes('crawler') || ua.includes('spider')) {
        return { isBot: true, botName: 'Bot' };
      }
      
      return { isBot: false, botName: null };
    } catch (_) {
      // Fallback in case of any error
      return { isBot: false, botName: null };
    }
  };

  // Detect if log is from a bot/crawler (backward compatibility)
  const isBotRequest = (message: string): boolean => {
    return detectBotType(message).isBot;
  };

  // Extract readable message from log entry
  const getReadableMessage = (message: string): { text: string; httpInfo?: { statusCode?: number; method?: string; path?: string } } => {
    try {
      const parsed = JSON.parse(message);
      // Try to extract the actual message text from various possible fields
      if (typeof parsed === 'object' && parsed !== null) {
        let messageText = '';
        // Check common message fields
        if (parsed.message && typeof parsed.message === 'string') {
          messageText = parsed.message;
        } else if (parsed.msg && typeof parsed.msg === 'string') {
          messageText = parsed.msg;
        } else if (parsed.text && typeof parsed.text === 'string') {
          messageText = parsed.text;
        } else if (parsed.content && typeof parsed.content === 'string') {
          messageText = parsed.content;
        } else {
          // If no message field, try to stringify the important parts
          const importantFields = ['message', 'msg', 'text', 'content', 'error', 'exception'];
          const readableParts: string[] = [];
          for (const field of importantFields) {
            if (parsed[field] && typeof parsed[field] === 'string') {
              readableParts.push(parsed[field]);
            }
          }
          if (readableParts.length > 0) {
            messageText = readableParts.join(' | ');
          } else {
            // Fallback: return a summary
            messageText = JSON.stringify(parsed).substring(0, 200);
          }
        }
        
        // Extract HTTP info from the message text
        const httpInfo = extractHttpInfo(messageText);
        return {
          text: messageText,
          httpInfo: httpInfo.statusCode ? httpInfo : undefined,
        };
      }
      
      const httpInfo = extractHttpInfo(message);
      return {
        text: message,
        httpInfo: httpInfo.statusCode ? httpInfo : undefined,
      };
    } catch (_) {
      // Not JSON, extract HTTP info from raw message
      const httpInfo = extractHttpInfo(message);
      return {
        text: message,
        httpInfo: httpInfo.statusCode ? httpInfo : undefined,
      };
    }
  };

  // Render JSON value nicely
  const renderJsonValue = (value: any, depth = 0): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400">null</span>;
    }

    if (typeof value === 'boolean') {
      return <span className={value ? 'text-green-600' : 'text-red-600'}>{value.toString()}</span>;
    }

    if (typeof value === 'string') {
      // Check if it's a date string
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return (
              <span className="text-blue-600">
                "{value}" <span className="text-gray-500 text-sm">({date.toLocaleString()})</span>
              </span>
            );
          }
        } catch (_) {}
      }
      return <span className="text-green-600">"{value}"</span>;
    }

    if (typeof value === 'number') {
      return <span className="text-purple-600">{value}</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-400">[]</span>;
      }
      return (
        <div className="ml-4">
          <div className="text-gray-600">[</div>
          {value.map((item, index) => (
            <div key={index} className="ml-4">
              <span className="text-gray-500">{index}:</span> {renderJsonValue(item, depth + 1)}
            </div>
          ))}
          <div className="text-gray-600">]</div>
        </div>
      );
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) {
        return <span className="text-gray-400">{'{}'}</span>;
      }
      return (
        <div className="ml-4">
          <div className="text-gray-600">{'{'}</div>
          {keys.map(objKey => (
            <div key={objKey} className="ml-4">
              <span className="text-blue-700 dark:text-blue-400 font-medium">"{objKey}"</span>
              <span className="text-gray-600">: </span>
              {renderJsonValue(value[objKey], depth + 1)}
            </div>
          ))}
          <div className="text-gray-600">{'}'}</div>
        </div>
      );
    }

    return <span>{String(value)}</span>;
  };

  // Fetch AI analysis
  const fetchAIAnalysis = async () => {
    if (!selectedLog || !companyId || !selectedDataSource?.id) return;
    
    setAiLoading(true);
    setAiError(null);
    
    try {
      const response = await api.post('/api/v1/betterstack/analyze', {
        companyId,
        dataSourceId: selectedDataSource.id,
        logId: selectedLog._id,
        message: selectedLog.message,
        severity: selectedLog.severity || selectedLog.level || 'info',
        source: selectedLog.source,
        service: selectedLog.service,
        host: selectedLog.host,
        timestamp: selectedLog.timestamp,
      });
      
      setAiAnalysis(response.data.data?.analysis || response.data.data?.explanation || 'No analysis available');
    } catch (error: any) {
      console.error('AI analysis error:', error);
      setAiError(error.response?.data?.message || 'Failed to analyze log. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  // Auto-fetch AI analysis when explain tab is opened
  React.useEffect(() => {
    if (detailTab === 'explain' && selectedLog && !aiAnalysis && !aiLoading && !aiError) {
      fetchAIAnalysis();
    }
  }, [detailTab, selectedLog]);

  // Reset AI state when log changes
  React.useEffect(() => {
    setAiAnalysis(null);
    setAiError(null);
  }, [selectedLog?._id]);

  // Get severity badge color
  const getSeverityBadgeColor = (severity?: string) => {
    const normalizedSeverity = severity?.toLowerCase();
    switch (normalizedSeverity) {
      case 'critical':
      case 'emergency':
      case 'alert':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'error':
        return 'bg-red-50 text-red-700 dark:bg-red-900/10 dark:text-red-500';
      case 'warning':
        return 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/10 dark:text-yellow-500';
      case 'notice':
      case 'info':
        return 'bg-blue-50 text-blue-700 dark:bg-blue-900/10 dark:text-blue-500';
      case 'debug':
        return 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
      default:
        return 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  if (!selectedDataSource) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No BetterStack Data Source Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {betterstackDataSources.length === 0
              ? 'No BetterStack data sources are configured for your company.'
              : 'Unable to access BetterStack data sources.'}
          </p>
          {betterstackDataSources.length === 0 && (
            <p className="text-sm text-gray-500">
              Please contact your administrator to configure a BetterStack data source.
            </p>
          )}
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs text-left">
            <p className="font-semibold mb-1">Debug Info:</p>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>Company ID: {companyId || 'Not set'}</li>
              <li>Selected Data Sources: {selectedDataSources.length}</li>
              <li>BetterStack Sources: {betterstackDataSources.length}</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">BetterStack Logs</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-2">
            View and search logs synced from BetterStack
            {connected && (
              <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 text-xs">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Live updates enabled
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      {statsData?.data && statsData.data.totalLogs !== undefined && (
        <div className="space-y-4">
          {/* Primary Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Logs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Logs</div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                    {statsData.data.totalLogs?.toLocaleString() || 0}
                  </div>
                </div>
                <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </div>

            {/* Errors */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Errors</div>
                  <div className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">
                    {(statsData.data.errorCount || statsData.data.bySeverity?.error || statsData.data.bySeverity?.critical || 0).toLocaleString()}
                  </div>
                </div>
                <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
            </div>

            {/* Warnings */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Warnings</div>
                  <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mt-2">
                    {(statsData.data.warningCount || statsData.data.bySeverity?.warning || statsData.data.bySeverity?.notice || 0).toLocaleString()}
                  </div>
                </div>
                <div className="p-3 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>
            </div>

            {/* Success Rate */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Success Rate</div>
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
                    {statsData.data.totalLogs > 0
                      ? Math.round(((statsData.data.successCount || 0) / statsData.data.totalLogs) * 100)
                      : 0}%
                  </div>
                </div>
                <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                  <Info className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Secondary Stats Row - HTTP Methods & Status Codes */}
          {(statsData.data.byMethod || statsData.data.byStatusCode) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* HTTP Methods */}
              {statsData.data.byMethod && Object.keys(statsData.data.byMethod).length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white mb-3">HTTP Methods</div>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(statsData.data.byMethod)
                      .sort(([, a], [, b]) => (b as number) - (a as number))
                      .map(([method, count]) => (
                        <div
                          key={method}
                          className={`px-3 py-2 rounded-lg ${getHttpMethodColor(method)}`}
                        >
                          <span className="font-semibold">{method}</span>
                          <span className="ml-2 text-xs opacity-75">{count.toLocaleString()}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* HTTP Status Codes */}
              {statsData.data.byStatusCode && Object.keys(statsData.data.byStatusCode).length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Status Codes</div>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(statsData.data.byStatusCode)
                      .sort(([, a], [, b]) => (b as number) - (a as number))
                      .slice(0, 10)
                      .map(([status, count]) => (
                        <div
                          key={status}
                          className={`px-3 py-2 rounded-lg ${getHttpStatusColor(parseInt(status))}`}
                        >
                          <span className="font-semibold">{status}</span>
                          <span className="ml-2 text-xs opacity-75">{count.toLocaleString()}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Histogram Chart - Log Distribution Over Time */}
      {histogramData?.data?.data && histogramData.data.data.length > 0 && (() => {
        // Collect all unique status codes and create color mapping
        const statusCodeSet = new Set<string>();
        histogramData.data.data.forEach((item) => {
          Object.keys(item.statusCodes || {}).forEach((code) => {
            if (code !== 'unknown') statusCodeSet.add(code);
          });
        });
        
        const statusCodes = Array.from(statusCodeSet).sort((a, b) => parseInt(a) - parseInt(b));
        
        // Color mapping for status codes
        const statusCodeColors: Record<string, string> = {
          '200': '#10b981', // Green for success
          '201': '#10b981',
          '202': '#10b981',
          '204': '#10b981',
          '301': '#3b82f6', // Blue for redirects
          '302': '#3b82f6',
          '303': '#3b82f6',
          '304': '#3b82f6',
          '400': '#f59e0b', // Orange for client errors
          '401': '#f59e0b',
          '403': '#f59e0b',
          '404': '#f59e0b',
          '500': '#ef4444', // Red for server errors
          '502': '#ef4444',
          '503': '#ef4444',
        };
        
        const getStatusCodeColor = (code: string): string => {
          return statusCodeColors[code] || '#6b7280'; // Gray for unknown codes
        };
        
        // Prepare chart data with status code counts
        const chartData = histogramData.data.data.map((item) => {
          const date = new Date(item.timestamp);
          let timeLabel: string;
          
          if (histogramData.data.interval === 'day') {
            timeLabel = date.toLocaleDateString('en-GB', {
              month: 'short',
              day: 'numeric',
            });
          } else {
            // Hourly format: always show date and time so it's clear which day each hour belongs to
            timeLabel = date.toLocaleString('en-GB', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });
          }
          
          const dataPoint: Record<string, any> = {
            time: timeLabel,
            timestamp: item.timestamp,
            count: item.count,
          };
          
          // Add status code counts
          statusCodes.forEach((code) => {
            dataPoint[`status_${code}`] = item.statusCodes?.[code] || 0;
          });
          
          return dataPoint;
        });
        
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="px-4 pt-4 pb-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Log Distribution Over Time
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Showing log volume by {histogramData.data.interval === 'day' ? 'day' : 'hour'}
              </p>
            </div>
            <div className="px-4 pb-0">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={chartData}
                  margin={{ top: 8, right: 16, left: 0, bottom: 10 }}
                  barCategoryGap="15%"
                  barGap={2}
                >
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke="#e5e7eb" 
                    vertical={false}
                    className="dark:stroke-gray-700" 
                  />
                  <XAxis
                    dataKey="time"
                    stroke="#d1d5db"
                    tickLine={false}
                    axisLine={false}
                    angle={0}
                    textAnchor="middle"
                    height={20}
                    interval="preserveStartEnd"
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    className="dark:fill-gray-400"
                  />
                  <YAxis 
                    stroke="#d1d5db"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    className="dark:fill-gray-400"
                    width={45}
                    tickFormatter={(value) => {
                      if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
                      return value.toString();
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                      fontSize: '12px',
                    }}
                    labelStyle={{ 
                      color: '#111827', 
                      fontWeight: '600', 
                      marginBottom: '2px', 
                      fontSize: '12px' 
                    }}
                    itemStyle={{
                      color: '#374151',
                      fontSize: '12px',
                      padding: '2px 0',
                    }}
                    formatter={(value: number, name: string) => {
                      const code = name.replace('status_', '');
                      return [`${value.toLocaleString()}`, code];
                    }}
                    separator=": "
                  />
                  <Legend
                    wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                    iconType="square"
                    formatter={(value: string) => {
                      const code = value.replace('status_', '');
                      return code;
                    }}
                  />
                  {statusCodes.map((code) => (
                    <Bar
                      key={code}
                      dataKey={`status_${code}`}
                      fill={getStatusCodeColor(code)}
                      radius={[4, 4, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })()}

      {/* Filters - BetterStack Style */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        {/* Top Bar - Search and Actions */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            {/* Left: Search Bar */}
            <div className="flex-1 w-full md:w-auto">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Search logs, methods (GET/POST), IPs, URLs, status codes, user agents..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    className="input pl-10 pr-10 w-full bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-600"
                  />
                  {searchQuery && searchQuery !== debouncedSearchQuery && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
                    </div>
                  )}
                  {searchQuery && searchQuery === debouncedSearchQuery && debouncedSearchQuery && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setDebouncedSearchQuery('');
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Date Range, Export, and Sync */}
            <div className="flex items-center gap-3">
              <DateRangeFilter
                value={dateRange}
                onChange={(range) => {
                  setDateRange(range);
                  setPage(1);
                }}
              />
              <button
                onClick={exportToCSV}
                disabled={!logsData?.data?.logs || logsData.data.logs.length === 0}
                className="btn btn-secondary flex items-center gap-2"
                title="Export filtered logs to CSV"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending}
                className="btn btn-secondary flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                {syncMutation.isPending ? 'Syncing...' : 'Sync Now'}
              </button>
            </div>
          </div>
        </div>

        {/* Filter Row */}
        <div className="p-4">
          {(() => {
            const filterOptions = getFilterOptions();
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
                {/* Severity Filter */}
                <div>
                  <select
                    value={severityFilter}
                    onChange={(e) => {
                      setSeverityFilter(e.target.value);
                      setPage(1);
                    }}
                    className="input w-full bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-sm"
                  >
                    <option value="">All Severities</option>
                    {filterOptions.severities.map((severity) => (
                      <option key={severity} value={severity}>
                        {severity.charAt(0).toUpperCase() + severity.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Method Filter */}
                <div>
                  <select
                    value={methodFilter}
                    onChange={(e) => {
                      setMethodFilter(e.target.value);
                      setPage(1);
                    }}
                    className="input w-full bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-sm"
                  >
                    <option value="">All Methods</option>
                    {filterOptions.methods.map((method) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Source Filter */}
                <div>
                  <select
                    value={sourceFilter}
                    onChange={(e) => {
                      setSourceFilter(e.target.value);
                      setPage(1);
                    }}
                    className="input w-full bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-sm"
                  >
                    <option value="">All Sources</option>
                    {filterOptions.sources.map((source) => (
                      <option key={source} value={source}>
                        {source}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Service and Host filters removed - these fields aren't populated by BetterStack */}

                {/* Status Code Filter */}
                <div>
                  <select
                    value={statusCodeFilter}
                    onChange={(e) => {
                      // Ensure we only use the code value, not the display text
                      const selectedValue = e.target.value;
                      // Extract just the numeric code if somehow description got included
                      const codeOnly = selectedValue.match(/^\d{3}$/)?.[0] || selectedValue;
                      setStatusCodeFilter(codeOnly);
                      setPage(1);
                    }}
                    className="input w-full bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-sm"
                  >
                    <option value="">All Status Codes</option>
                    {filterOptions.statusCodes?.map((code) => {
                      const description = getStatusCodeDescription(code);
                      return (
                        <option key={code} value={code}>
                          {code} ({description})
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Bot Filter */}
                <div>
                  <select
                    value={botFilter}
                    onChange={(e) => {
                      setBotFilter(e.target.value);
                      setPage(1);
                    }}
                    className="input w-full bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-sm"
                  >
                    <option value="all">All Requests</option>
                    <option value="bots">🤖 All Bots</option>
                    <option value="humans">👤 Humans Only</option>
                    {filterOptions.botTypes && filterOptions.botTypes.length > 0 && (
                      <>
                        <optgroup label="Specific Bots">
                          {filterOptions.botTypes.map((botType) => {
                            const botLabels: Record<string, string> = {
                              'googlebot': '🔍 Googlebot',
                              'bingbot': '🔎 Bingbot',
                              'facebookbot': '📘 Facebook Bot',
                              'twitterbot': '🐦 Twitter Bot',
                              'linkedinbot': '💼 LinkedIn Bot',
                              'baiduspider': '🇨🇳 Baidu Spider',
                              'yandexbot': '🇷🇺 Yandex Bot',
                              'duckduckbot': '🦆 DuckDuckGo Bot',
                              'slurp': '📰 Yahoo Slurp',
                              'sogou': '🇨🇳 Sogou',
                              'ahrefs': '🔗 Ahrefs Bot',
                              'semrush': '📊 SEMrush Bot',
                              'mj12bot': '🕷️ MJ12 Bot',
                              'exabot': '🤖 ExaBot',
                              'ia_archiver': '📚 Archive.org',
                              'chatgpt': '🤖 ChatGPT',
                              'openai': '🤖 OpenAI',
                              'other': '🤖 Other Bots',
                            };
                            return (
                              <option key={botType} value={botType}>
                                {botLabels[botType] || `🤖 ${botType.charAt(0).toUpperCase() + botType.slice(1)}`}
                              </option>
                            );
                          })}
                        </optgroup>
                      </>
                    )}
                  </select>
                </div>

                {/* Clear Filters */}
                {(searchQuery || severityFilter || methodFilter || sourceFilter || serviceFilter || hostFilter || statusCodeFilter || botFilter) && (
                  <div>
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setDebouncedSearchQuery('');
                        setSeverityFilter('');
                        setMethodFilter('');
                        setSourceFilter('');
                        setServiceFilter('');
                        setHostFilter('');
                        setStatusCodeFilter('');
                        setBotFilter('');
                        setPage(1);
                      }}
                      className="btn btn-secondary btn-sm w-full text-sm"
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Results Count - Show when filters are active */}
      {logsData?.data && (debouncedSearchQuery || severityFilter || methodFilter || sourceFilter || serviceFilter || hostFilter || statusCodeFilter || (botFilter && botFilter !== 'all')) && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {logsData.data.total.toLocaleString()} {logsData.data.total === 1 ? 'result' : 'results'} found
              </span>
              {(debouncedSearchQuery || severityFilter || methodFilter || sourceFilter || serviceFilter || hostFilter || (botFilter && botFilter !== 'all')) && (
                <span className="text-xs text-blue-700 dark:text-blue-300">
                  {logsData.data.totalPages > 1 && `(${logsData.data.totalPages} pages)`}
                </span>
              )}
            </div>
            {logsData.data.total > 0 && (
              <div className="text-xs text-blue-700 dark:text-blue-300">
                Showing page {page} of {logsData.data.totalPages}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Logs Table - BetterStack Style */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort('timestamp')}
                >
                  <div className="flex items-center gap-2">
                    Time
                    {sortBy === 'timestamp' && (
                      <span className="text-gray-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort('level')}
                >
                  <div className="flex items-center gap-2">
                    Severity
                    {sortBy === 'level' && (
                      <span className="text-gray-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort('method')}
                >
                  <div className="flex items-center gap-2">
                    Method
                    {sortBy === 'method' && (
                      <span className="text-gray-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Message
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort('source')}
                >
                  <div className="flex items-center gap-2">
                    Source
                    {sortBy === 'source' && (
                      <span className="text-gray-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort('service')}
                >
                  <div className="flex items-center gap-2">
                    Service
                    {sortBy === 'service' && (
                      <span className="text-gray-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort('host')}
                >
                  <div className="flex items-center gap-2">
                    Host
                    {sortBy === 'host' && (
                      <span className="text-gray-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => handleSort('bot')}
                >
                  <div className="flex items-center gap-2">
                    Bot
                    {sortBy === 'bot' && (
                      <span className="text-gray-400">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {logsLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    Loading logs...
                  </td>
                </tr>
              ) : logsError ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-red-500">
                    Error loading logs. Please try again.
                  </td>
                </tr>
              ) : !logsData?.data?.logs || !Array.isArray(logsData.data.logs) || logsData.data.logs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    {logsData?.data?.total === 0 && !logsLoading ? (
                      <div className="text-center py-8">
                        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          No logs found
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                          {syncMutation.error?.response?.data?.error?.code === 'MISSING_QUERY_API_CREDENTIALS' ||
                          syncMutation.error?.response?.data?.message?.includes('Query API credentials') ? (
                            <>
                              Query API credentials are required to sync logs from BetterStack.
                              <br />
                              <a
                                href="https://betterstack.com/docs/logs/query-api/v2/dashboards/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-500 underline mt-2 inline-block"
                              >
                                Learn how to get them →
                              </a>
                            </>
                          ) : debouncedSearchQuery ? (
                            <>
                              No logs found matching <strong>"{debouncedSearchQuery}"</strong>.
                              <br />
                              <span className="text-sm mt-2 block">
                                Try adjusting your search terms or clearing filters. Search looks in: message content, HTTP methods (GET/POST), IP addresses, URLs, status codes, source, service, host, and user agents.
                              </span>
                            </>
                          ) : (
                            'Try syncing logs or adjusting your filters.'
                          )}
                        </p>
                      </div>
                    ) : (
                      'No logs found. Try syncing logs or adjusting your filters.'
                    )}
                  </td>
                </tr>
              ) : (
                logsData.data.logs.map((log) => {
                  const { service: extractedService, host: extractedHost } = extractServiceAndHost(log.message || '');
                  const service = log.service || extractedService;
                  const host = log.host || extractedHost;
                  const botDetection = detectBotType(log.message || '');
                  const isBot = botDetection?.isBot || false;
                  const botName = botDetection?.botName || null;
                  
                  return (
                  <tr
                    key={log._id}
                    onClick={() => setSelectedLog(log)}
                    className={`transition-colors cursor-pointer ${
                      selectedLog?._id === log._id
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500'
                        : isBot
                        ? 'bg-orange-50 dark:bg-orange-900/10 border-l-2 border-l-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <td className="px-4 py-3 text-sm font-mono text-gray-700 dark:text-gray-300">
                      {new Date(log.timestamp).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-3">
                      {(log.severity || log.level) && (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${getSeverityBadgeColor(
                            log.severity || log.level || 'info'
                          )}`}
                        >
                          {getSeverityIcon(log.severity || log.level || 'info')}
                          {(log.severity || log.level || 'info').toUpperCase()}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const { method } = extractHttpInfo(log.message);
                        if (method) {
                          return (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${getHttpMethodColor(
                                method
                              )}`}
                            >
                              {method}
                            </span>
                          );
                        }
                        return <span className="text-xs text-gray-400">-</span>;
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-md">
                      {(() => {
                        const { text, httpInfo } = getReadableMessage(log.message);
                        return (
                          <div className="flex items-center gap-2">
                            <div className="truncate flex-1" title={text}>
                              {text}
                            </div>
                            {httpInfo?.statusCode && (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${getHttpStatusColor(
                                  httpInfo.statusCode
                                )}`}
                                title={`HTTP ${httpInfo.statusCode}${httpInfo.method ? ` ${httpInfo.method}` : ''}${httpInfo.path ? ` ${httpInfo.path}` : ''}`}
                              >
                                {httpInfo.statusCode}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 font-medium">
                      {log.source || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {service || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {host || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {isBot && botName ? (
                        <div className="flex items-center gap-2">
                          <span className="text-lg" title={`Bot: ${botName}`}>🤖</span>
                          <span className="text-xs font-medium text-orange-700 dark:text-orange-400">
                            {botName || 'Bot'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {logsData?.data && logsData.data.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, logsData.data.total)} of{' '}
              {logsData.data.total.toLocaleString()} {logsData.data.total === 1 ? 'log' : 'logs'}
              {debouncedSearchQuery && (
                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                  (matching "{debouncedSearchQuery}")
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="btn btn-secondary btn-sm"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {page} of {logsData.data.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= logsData.data.totalPages}
                className="btn btn-secondary btn-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Export Progress Modal */}
      {exportModalOpen && exportProgress && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => {
          if (exportProgress.status === 'completed' || exportProgress.status === 'error') {
            setExportModalOpen(false);
          }
        }}>
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Exporting Logs to CSV
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {exportProgress.status === 'fetching' && 'Fetching logs from server...'}
                  {exportProgress.status === 'processing' && 'Processing logs into CSV files...'}
                  {exportProgress.status === 'completed' && 'Export completed successfully!'}
                  {exportProgress.status === 'error' && 'Export failed'}
                </p>
              </div>
              {(exportProgress.status === 'completed' || exportProgress.status === 'error') && (
                <button
                  onClick={() => setExportModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {exportProgress.status === 'error' ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <p className="text-red-600 dark:text-red-400">{exportProgress.error}</p>
                </div>
              ) : (
                <>
                  {/* Overall Progress */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Overall Progress
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {exportProgress.fetched.toLocaleString()} / {exportProgress.total.toLocaleString()} logs
                        {exportProgress.status === 'fetching' && ` (Page ${exportProgress.currentPage}/${exportProgress.totalPages})`}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div
                        className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                        style={{
                          width: `${exportProgress.total > 0 ? (exportProgress.fetched / exportProgress.total) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Files Progress */}
                  {exportProgress.files.length > 1 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        Files ({exportProgress.files.length} files, {Math.ceil(exportProgress.total / 10000)} max per file)
                      </h3>
                      <div className="space-y-3">
                        {exportProgress.files.map((file, index) => (
                          <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {file.name}
                              </span>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {file.logs > 0 ? `${file.logs.toLocaleString()} logs` : 'Preparing...'}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                              <div
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  file.progress === 100 ? 'bg-green-500' : 'bg-blue-500'
                                }`}
                                style={{ width: `${file.progress}%` }}
                              />
                            </div>
                            {file.blob && (
                              <button
                                onClick={() => downloadExportFile(index)}
                                className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                              >
                                <Download className="w-3 h-3" />
                                Download
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Single File Progress */}
                  {exportProgress.files.length === 1 && exportProgress.files[0].blob && (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {exportProgress.files[0].logs.toLocaleString()} logs ready for download
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            {exportProgress.status === 'completed' && (
              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {exportProgress.files.length} file{exportProgress.files.length !== 1 ? 's' : ''} ready
                </div>
                <div className="flex gap-2">
                  {exportProgress.files.length > 1 && (
                    <button
                      onClick={downloadAllFiles}
                      className="btn btn-primary btn-sm flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download All
                    </button>
                  )}
                  {exportProgress.files.length === 1 && (
                    <button
                      onClick={() => downloadExportFile(0)}
                      className="btn btn-primary btn-sm flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download CSV
                    </button>
                  )}
                  <button
                    onClick={() => setExportModalOpen(false)}
                    className="btn btn-secondary btn-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Log Detail Modal - BetterStack Style */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/50" onClick={() => setSelectedLog(null)}>
          <div 
            className="h-full w-full md:w-2/3 lg:w-1/2 bg-white dark:bg-gray-900 shadow-xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Log {new Date(selectedLog.timestamp).toLocaleString()}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {selectedLog.source || 'Unknown source'}
                </p>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setDetailTab('overview')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  detailTab === 'overview'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Overview
                </div>
              </button>
              <button
                onClick={() => setDetailTab('context')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  detailTab === 'context'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Context
                </div>
              </button>
              <button
                onClick={() => setDetailTab('explain')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  detailTab === 'explain'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Explain with AI
                </div>
              </button>
              <button
                onClick={() => setDetailTab('comments')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  detailTab === 'comments'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Comments
                </div>
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {detailTab === 'overview' && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">JSON</h3>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                      <pre className="text-xs overflow-x-auto">
                        <code className="text-gray-900 dark:text-gray-100">
                          {JSON.stringify({
                            dt: selectedLog.timestamp,
                            level: selectedLog.severity || 'info',
                            message: selectedLog.message,
                            source: selectedLog.source,
                            service: selectedLog.service,
                            host: selectedLog.host,
                            metadata: selectedLog.metadata,
                            syncedAt: selectedLog.syncedAt,
                          }, null, 2)}
                        </code>
                      </pre>
                    </div>
                  </div>
                </div>
              )}

              {detailTab === 'context' && (() => {
                const { parsed, isJson } = parseMessage(selectedLog.message);
                
                // Extract values from parsed JSON or use log fields
                const getValue = (paths: string[], fallback?: string): string => {
                  if (isJson && parsed && typeof parsed === 'object') {
                    for (const path of paths) {
                      const keys = path.split('.');
                      let value: any = parsed;
                      for (const key of keys) {
                        if (value && typeof value === 'object' && key in value) {
                          value = value[key];
                        } else {
                          value = null;
                          break;
                        }
                      }
                      if (value !== null && value !== undefined && value !== '') {
                        return String(value);
                      }
                    }
                  }
                  return fallback || '-';
                };
                
                const host = getValue(
                  ['syslog.hostname', 'syslog.host', 'hostname', 'host'],
                  selectedLog.host
                );
                
                const service = getValue(
                  ['syslog.appname', 'syslog.app', 'service', 'app'],
                  selectedLog.service
                );
                
                const env = getValue(
                  ['env', 'environment'],
                  undefined
                );
                
                const sourceIp = getValue(
                  ['syslog.source_ip', 'source_ip', 'sourceIp', 'ip'],
                  undefined
                );
                
                const platform = getValue(
                  ['platform'],
                  undefined
                );
                
                const facility = getValue(
                  ['syslog.facility', 'facility'],
                  undefined
                );
                
                const process = getValue(
                  ['syslog.procid', 'syslog.process', 'process', 'pid'],
                  undefined
                );
                
                return (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Timestamp</h3>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {isJson && parsed?.dt 
                            ? new Date(parsed.dt).toLocaleString() 
                            : new Date(selectedLog.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Severity</h3>
                        <p className="text-sm text-gray-900 dark:text-white capitalize">
                          {isJson && parsed?.level ? parsed.level : (selectedLog.severity || selectedLog.level || 'info')}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Source</h3>
                        <p className="text-sm text-gray-900 dark:text-white">{selectedLog.source || '-'}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Service</h3>
                        <p className="text-sm text-gray-900 dark:text-white">{service}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Host</h3>
                        <p className="text-sm text-gray-900 dark:text-white">{host}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Environment</h3>
                        <p className="text-sm text-gray-900 dark:text-white">{env}</p>
                      </div>
                      {sourceIp && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Source IP</h3>
                          <p className="text-sm text-gray-900 dark:text-white">{sourceIp}</p>
                        </div>
                      )}
                      {platform && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Platform</h3>
                          <p className="text-sm text-gray-900 dark:text-white">{platform}</p>
                        </div>
                      )}
                      {facility && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Facility</h3>
                          <p className="text-sm text-gray-900 dark:text-white">{facility}</p>
                        </div>
                      )}
                      {process && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Process</h3>
                          <p className="text-sm text-gray-900 dark:text-white">{process}</p>
                        </div>
                      )}
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Synced At</h3>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {selectedLog.syncedAt && selectedLog.syncedAt !== 'Invalid Date' 
                            ? new Date(selectedLog.syncedAt).toLocaleString() 
                            : '-'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Message</h3>
                      {isJson ? (
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 overflow-x-auto">
                          <div className="text-xs font-mono">
                            {renderJsonValue(parsed)}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                          <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap font-mono">{selectedLog.message}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {detailTab === 'explain' && (
                <div className="space-y-4">
                  {aiLoading ? (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <Sparkles className="w-5 h-5 text-blue-500 animate-pulse" />
                        <div>
                          <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                            Analyzing log with AI...
                          </h3>
                          <p className="text-sm text-blue-800 dark:text-blue-200">
                            Please wait while we analyze this log entry.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : aiError ? (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-red-900 dark:text-red-100 mb-2">
                            Analysis Error
                          </h3>
                          <p className="text-sm text-red-800 dark:text-red-200 mb-3">
                            {aiError}
                          </p>
                          <button
                            onClick={fetchAIAnalysis}
                            className="btn btn-secondary btn-sm"
                          >
                            Try Again
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : aiAnalysis ? (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <Sparkles className="w-5 h-5 text-blue-500 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                            AI Analysis
                          </h3>
                          <div className="text-sm text-blue-800 dark:text-blue-200 whitespace-pre-wrap">
                            {aiAnalysis}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={fetchAIAnalysis}
                        className="btn btn-secondary btn-sm mt-3 flex items-center gap-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Refresh Analysis
                      </button>
                    </div>
                  ) : (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-blue-500 mt-0.5" />
                        <div>
                          <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                            AI Explanation
                          </h3>
                          <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                            Click the button below to analyze this log entry with AI.
                          </p>
                          <button
                            onClick={fetchAIAnalysis}
                            className="btn btn-primary btn-sm flex items-center gap-2"
                          >
                            <Sparkles className="w-4 h-4" />
                            Analyze with AI
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {detailTab === 'comments' && (
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-3">
                      <MessageSquare className="w-4 h-4 text-gray-400" />
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">Comments</h3>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Comments feature coming soon. You'll be able to add notes and collaborate on log entries.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BetterStackPage;
